import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../config/config.js";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import FasesArea from "../area/FasesArea/FasesArea";
import ProgramacionArea from "../area/ProgramacionArea/ProgramacionArea";
import ProgramacionIndividualArea from "../area/ProgramacionIndividualArea/ProgramacionIndividualArea";
import ConfigCategoryArea from "../area/ConfigCategoryArea/ConfigCategoryArea";
import ResultadosArea from "../area/ResultadosArea/ResultadosArea";
import InstitucionesArea from "../area/InstitucionesArea/InstitucionesArea.jsx";
import CategoriasIndividualesArea from "../area/CategoriasIndividualesArea/CategoriasIndividualesArea.jsx";
import PosicionesArea from "../area/PosicionesArea/PosicionesArea";
import AtletismoResultadosArea from "../area/AtletismoResultadosArea/AtletismoResultadosArea.jsx";
import { useAthleticsResultsStatus } from "../hooks/useAthleticsResultsStatus.js";
import { useAthleticsPruebas, useAthleticsResultsActions } from "../hooks/useAthleticsResults.js";
import { getAthleticsPrueba } from "../services/athleticsResultsService.js";

import { IoChevronBack } from "react-icons/io5";
import { FaExternalLinkAlt } from "react-icons/fa";
import SportSwitcher from "../components/SportSwitcher/SportSwitcher.jsx";
import { getMenuSportsForEvent } from "../services/menuSportsService.js";

import styled from "@emotion/styled";

const CustomTabList = styled(TabList)`
  .MuiTab-root {
    min-width: 100px;
    color: #616161;
    font-weight: bold;
    text-transform: uppercase;
    transition: color 0.3s;
    &:hover {
      color: #1e88e5;
    }
    &.Mui-selected {
      color: #1e88e5;
    }
  }
  .MuiTabs-indicator {
    background-color: #1e88e5;
    height: 4px;
  }
`;

const CustomTabPanel = styled(TabPanel)`
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
`;

// Pestaña "Resultados" de Atletismo en deportes individuales. Solo existe cuando el backend confirma
// el módulo (GET .../athletics-results/.../status → enabled); ante cualquier error queda oculta.
// "5" es "Instituciones" en ambos tipos de deporte (al cambiar de deporte se queda en esa pestaña).
const ATHLETICS_TAB_VALUE = "7";
// Pestaña "Categorías" de deportes individuales: inscritos por categoría (agregar, retirar, mover).
const CATEGORIES_TAB_VALUE = "8";

const CategoriaSection = () => {
  const [value, setValue] = useState("1");
  const { idevent, idsport } = useParams();
  const [event, setEvent] = useState(null);
  const [sport, setSport] = useState(null);
  const [sportIdentity, setSportIdentity] = useState(null);
  const [eventSports, setEventSports] = useState([]);
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // `sport` no se limpia al cambiar de deporte: mientras llega el nuevo, no se confía en el anterior.
  const sportMatchesRoute =
    Boolean(sport) &&
    (sport.idsport === undefined ||
      sport.idsport === null ||
      Number(sport.idsport) === Number(idsport));
  const isIndividualSport = sportMatchesRoute && sport.typeSport === 3;
  const athleticsStatus = useAthleticsResultsStatus({
    idevent,
    idsport,
    enabled: isIndividualSport,
  });
  const showAthleticsTab = isIndividualSport && athleticsStatus.enabled;

  // Pestaña Categorías: las categorías ligadas a una prueba de atletismo agregan con la misma ventana de Resultados
  // (inscripción + lista de salida). Sin el módulo de atletismo la pestaña funciona como antes (solo inscripción).
  const athleticsPruebasQuery = useAthleticsPruebas({ idevent, idsport, enabled: showAthleticsTab });
  const athleticsActions = useAthleticsResultsActions({ idevent, idsport });
  const athleticsPruebas = athleticsPruebasQuery.data?.pruebas;
  const athleticsForCategories = useMemo(
    () =>
      showAthleticsTab
        ? {
            pruebas: athleticsPruebas || [],
            canWrite: athleticsStatus.canWrite === true,
            loadPrueba: (pruebaId) => getAthleticsPrueba({ idevent, idsport, pruebaId }),
            addAthlete: athleticsActions.addAthlete,
            searchCandidates: athleticsActions.searchCandidates,
          }
        : null,
    [athleticsActions.addAthlete, athleticsActions.searchCandidates, athleticsPruebas, athleticsStatus.canWrite, idevent, idsport, showAthleticsTab]
  );

  // Marcas de atletismo sin guardar: se avisa antes de cambiar de pestaña, de deporte o de volver.
  const athleticsDirtyRef = useRef(false);
  const athleticsTabSelectedRef = useRef(false);
  const handleAthleticsDirtyChange = useCallback((dirty) => {
    athleticsDirtyRef.current = Boolean(dirty);
  }, []);
  const confirmLeaveAthletics = () =>
    !athleticsDirtyRef.current ||
    window.confirm("Hay marcas de atletismo sin guardar. ¿Salir y descartarlas?");

  const handleChange = (event, newValue) => {
    if (newValue !== value && !confirmLeaveAthletics()) return;
    // Pestañas que solo existen en deportes individuales: al cambiar a otro deporte se vuelve a la primera.
    athleticsTabSelectedRef.current =
      isIndividualSport && [ATHLETICS_TAB_VALUE, CATEGORIES_TAB_VALUE].includes(newValue);
    setValue(newValue);
  };

  // Si la pestaña de Atletismo desaparece (módulo apagado, otro deporte), no queda seleccionada.
  useEffect(() => {
    if (isIndividualSport && value === ATHLETICS_TAB_VALUE && !showAthleticsTab) {
      athleticsTabSelectedRef.current = false;
      setValue("1");
    }
  }, [isIndividualSport, showAthleticsTab, value]);

  // Al cambiar de deporte con la pestaña de Atletismo abierta se vuelve a la primera: los deportes
  // colectivos no la tienen.
  const previousSportRef = useRef(idsport);
  useEffect(() => {
    if (previousSportRef.current === idsport) return;
    previousSportRef.current = idsport;
    athleticsDirtyRef.current = false;
    if (athleticsTabSelectedRef.current) {
      athleticsTabSelectedRef.current = false;
      setValue("1");
    }
  }, [idsport]);
  const linkPublico = () => {
    window.open(
      `https://resultados.hayllis.com/torneos/${idevent}/sports/${idsport}/sport`,
      "_blank"
    );
  };

  useEffect(() => {
    const fetchDatos = async () => {
      try {
        let event = await axios.get(
          `${API_BASE_URL}/championship/?idevent=${idevent}`
        );
        setEvent(event.data);

        let sport = await axios.get(
          `${API_BASE_URL}/sport/?idsport=${idsport}`
        );
        setSport(sport.data);

        try {
          const configResponse = await axios.post(
            `${API_BASE_URL}/config_category`,
            {
              idevent: Number(idevent),
              idsport: Number(idsport),
            }
          );
          setSportIdentity(configResponse.data || null);
        } catch (configError) {
          if (configError?.response?.status !== 404) {
            console.error("Error fetching sport identity", configError);
          }
          setSportIdentity(null);
        }

        if (user?.iduser) {
          const menuSports = await getMenuSportsForEvent({
            iduser: user.iduser,
            idevent,
          });
          setEventSports(menuSports);
        }
      } catch (error) {
        console.error("Error fetching datos", error);
      }
    };
    if (idevent && idsport) {
      fetchDatos();
    }
  }, [idevent, idsport, user?.iduser]);

  useEffect(() => {
    const handleIdentityUpdated = (event) => {
      const detail = event.detail || {};
      if (
        Number(detail.idevent) !== Number(idevent) ||
        Number(detail.idsport) !== Number(idsport)
      ) {
        return;
      }
      setSportIdentity((current) => ({
        ...(current || {}),
        nameSport: detail.nameSport || null,
        sport_image: detail.sport_image || null,
      }));
      setEventSports((current) =>
        current.map((item) =>
          Number(item.idsport) === Number(idsport)
            ? {
                ...item,
                displayName: detail.nameSport || item.name,
                displayImage: detail.sport_image || item.image_path,
              }
            : item
        )
      );
    };
    window.addEventListener("winscore:sport-identity-updated", handleIdentityUpdated);
    return () =>
      window.removeEventListener(
        "winscore:sport-identity-updated",
        handleIdentityUpdated
      );
  }, [idevent, idsport]);

  const currentMenuSport = eventSports.find(
    (item) => Number(item.idsport) === Number(idsport)
  );
  const visibleSportName =
    sportIdentity?.nameSport ||
    currentMenuSport?.displayName ||
    sport?.name ||
    "TÍTULO";

  const handleSportSelect = (selectedSport) => {
    if (Number(selectedSport.idsport) === Number(idsport)) return;
    if (!confirmLeaveAthletics()) return;
    navigate(
      `/campeonatos/${idevent}/list-categorias/${selectedSport.idsport}/deporte`
    );
  };

  return (
    <div className="categoria_section flex flex-col items-center w-full font-quickSand">
      <div>
        <div className="text-center">
          <h2 className="text-3xl font-bold text-blue-600">
            {event && event.name}
          </h2>
        </div>
        <div className="flex items-center justify-center mt-4 space-x-3">
          {/* Ícono de retroceso, más grande y más oscuro */}
          <IoChevronBack
            onClick={() => {
              if (confirmLeaveAthletics()) navigate(-1);
            }}
            className="
      text-4xl text-gray-800 cursor-pointer
      transition-transform duration-200 ease-out
      hover:-translate-x-1 hover:scale-110
    "
          />

          {/* Título */}
          <h3 className="text-4xl font-extrabold text-gray-900 text-center">
            {visibleSportName.toUpperCase()}
          </h3>

          <SportSwitcher
            sports={eventSports}
            currentSportId={idsport}
            onSelect={handleSportSelect}
          />

          {/* Ícono de enlace externo, más grande, más oscuro y hover diagonal arriba-derecha */}
          <FaExternalLinkAlt
            onClick={linkPublico}
            className="
      text-3xl text-gray-800 cursor-pointer
      transition-transform duration-200 ease-out
      hover:translate-x-1 hover:-translate-y-1 hover:scale-110
    "
          />
        </div>



      </div>
      <div className="mt-4 w-full max-w-6xl">
        {
          // typeSport === 3: Deporte Individual
          sport && sport.typeSport === 3 ? (<>
            <Box sx={{ width: "100%", typography: "body1" }}>
              <TabContext value={value}>
                <Box className="overflow-x-auto w-full flex justify-center">
                  <CustomTabList
                    onChange={handleChange}
                    aria-label="lab API tabs example"
                    variant="scrollable"
                    scrollButtons="auto"
                    centered={false}
                    className="flex-nowrap"
                  >
                    <Tab
                      label="Documentos"
                      value="1"
                      className="flex-shrink-0 font-quickSand"
                    />
                    <Tab label="Información" value="2" className="flex-shrink-0" />
                    <Tab label="Instituciones" value="5" className="flex-shrink-0" />
                    <Tab label="Categorías" value={CATEGORIES_TAB_VALUE} className="flex-shrink-0" />
                    {/* <Tab label="Informes" value="3" className="flex-shrink-0" /> */}
                    {showAthleticsTab ? (
                      <Tab
                        label="Resultados"
                        value={ATHLETICS_TAB_VALUE}
                        className="flex-shrink-0"
                      />
                    ) : null}
                    <Tab
                      label="Configuración"
                      value="4"
                      className="flex-shrink-0"
                    />
                  </CustomTabList>
                </Box>
                <CustomTabPanel value="1">
                  <FasesArea />
                </CustomTabPanel>
                <CustomTabPanel value="2">
                  <ProgramacionIndividualArea />
                </CustomTabPanel>
                <CustomTabPanel value="5">
                  <InstitucionesArea individual />
                </CustomTabPanel>
                <CustomTabPanel value={CATEGORIES_TAB_VALUE}>
                  <CategoriasIndividualesArea key={`${idevent}-${idsport}`} athletics={athleticsForCategories} />
                </CustomTabPanel>
                {showAthleticsTab ? (
                  <CustomTabPanel value={ATHLETICS_TAB_VALUE}>
                    <AtletismoResultadosArea
                      key={`${idevent}-${idsport}`}
                      status={athleticsStatus}
                      onDirtyChange={handleAthleticsDirtyChange}
                    />
                  </CustomTabPanel>
                ) : null}
                {/* <CustomTabPanel value="3">
                  <ResultadosArea />
                </CustomTabPanel> */}
                <CustomTabPanel value="4">
                  <ConfigCategoryArea />
                </CustomTabPanel>
              </TabContext>
            </Box>
          </>) : (<>
            <Box sx={{ width: "100%", typography: "body1" }}>
              <TabContext value={value}>
                <Box className="overflow-x-auto w-full flex justify-center">
                  <CustomTabList
                    onChange={handleChange}
                    aria-label="lab API tabs example"
                    variant="scrollable"
                    scrollButtons="auto"
                    centered={false}
                    className="flex-nowrap"
                  >
                    <Tab
                      label="Fases"
                      value="1"
                      className="flex-shrink-0 font-quickSand"
                    />
                    <Tab label="Programación" value="2" className="flex-shrink-0" />
                    <Tab label="Resultados" value="3" className="flex-shrink-0" />
                    <Tab label="Posiciones" value="4" className="flex-shrik-0" />
                    <Tab label="Instituciones" value="5" className="flex-shrik-0" />
                    <Tab
                      label="Configuración"
                      value="6"
                      className="flex-shrink-0"
                    />
                  </CustomTabList>
                </Box>
                <CustomTabPanel value="1">
                  <FasesArea />
                </CustomTabPanel>
                <CustomTabPanel value="2">
                  <ProgramacionArea />
                </CustomTabPanel>
                <CustomTabPanel value="3">
                  {/* La clave reinicia por completo el estado ligado al deporte/evento (partido, modales, listas) al cambiar de ruta. */}
                  <ResultadosArea key={`${idevent}:${idsport}`} />
                </CustomTabPanel>
                <CustomTabPanel value="4">
                  <PosicionesArea />
                </CustomTabPanel>
                <CustomTabPanel value="5">
                  <InstitucionesArea />
                </CustomTabPanel>
                <CustomTabPanel value="6">
                  <ConfigCategoryArea />
                </CustomTabPanel>
              </TabContext>
            </Box>
          </>)
        }

      </div>
    </div>
  );
};

export default CategoriaSection;